import type { ReactNode } from 'react'
import { Anchor, Tooltip } from '@mantine/core'
import { buildMc1ClaimUrl } from '../../utils/mc1Url'
import { openExternalUrl } from '../../utils/openExternalUrl'

export interface Mc1CaseLinkProps {
  // Internal TOCCase id (ClaimResponse.caseId). 0 means no MC1 case is linked.
  caseId: number
  // Tooltip while the link works. The two unavailable reasons are shared, so only
  // the working case needs wording per caller.
  tooltip: string
  // What the link reads as — the claim number in the header, a labelled icon in the
  // Tasks tab.
  children: ReactNode
  // Link colour while enabled. Defaults to the pane-wide link colour.
  color?: string
}

// No nested ternaries (Sonar S3358).
function getTooltip(enabled: string, hasBaseUrl: boolean, hasCaseId: boolean): string {
  if (!hasBaseUrl) return 'MC1 base URL not configured'
  if (!hasCaseId) return 'No MC1 case linked to this claim'
  return enabled
}

/**
 * A deep link into a claim's case in MC1.
 *
 * Every MC1 link in the pane goes to the same case page and must behave identically —
 * same URL, same disabled rules, and the same `openExternalUrl` path, which matters
 * because `window.open` is suppressed in the desktop WebView2 host. That behaviour
 * lives here once so the header link and the Tasks tab link cannot drift apart; the
 * callers supply only the label and the tooltip.
 *
 * Disabled rather than hidden when it can't work, so the affordance stays put and the
 * tooltip explains why.
 */
export function Mc1CaseLink({ caseId, tooltip, children, color = 'var(--link-color)' }: Readonly<Mc1CaseLinkProps>) {
  const baseUrl = import.meta.env.VITE_MC1_BASE_URL as string | undefined
  const hasBaseUrl = Boolean(baseUrl)
  const hasCaseId = Boolean(caseId)
  const disabled = !hasBaseUrl || !hasCaseId

  const handleClick = () => {
    if (!disabled) openExternalUrl(buildMc1ClaimUrl(baseUrl!, caseId))
  }

  return (
    <Tooltip label={getTooltip(tooltip, hasBaseUrl, hasCaseId)} withArrow>
      <Anchor
        component="button"
        type="button"
        fz="xs"
        underline="always"
        c={disabled ? 'dimmed' : color}
        onClick={handleClick}
        aria-disabled={disabled}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
      >
        {children}
      </Anchor>
    </Tooltip>
  )
}
